import { Injectable } from '@angular/core';
import * as moment from 'moment';

import { CompanyNowService } from '@shared/common/company-now/company-now.service';

/** A relative date offer, before it is resolved against a concrete "today". */
export interface DatePresetSpec {
    /** Stable identity — the `trackBy` key, and the wire value where the API takes a preset code. */
    key: string;
    /** What the user reads: `Tomorrow`, `3 days`, `Next Monday`. */
    label: string;
    /**
     * Days after today. A function for offers that depend on which day it currently is
     * (e.g. `Next Monday`); it receives the company's today.
     */
    offsetDays: number | ((today: moment.Moment) => number);
}

/** A preset resolved against the company calendar, with every rendering a caller needs. */
export interface ResolvedDatePreset {
    key: string;
    label: string;
    /** The company calendar day the preset lands on, date-only. */
    date: moment.Moment;
    /** `Thu` — the weekday, for the nudge's pills. */
    weekday: string;
    /** `Sep 12` — the date, for the Snooze menus' right-hand column. */
    dateLabel: string;
    /** `2026-09-12` — the format every one of these APIs takes on the wire. */
    iso: string;
}

/**
 * Resolves "today + N days" offers — the Snooze menus' presets and the no-date nudge's pills —
 * against **the company calendar**, via [[CompanyNowService]].
 *
 * WHY THIS EXISTS
 * The same computation had been written three times, and two of the three were wrong in the same
 * two ways:
 *
 *  1. **They anchored on the browser clock** (`new Date()`), not the firm's. Task dates float on the
 *     company calendar — the locked decision in the Timezone Logic story — and both back ends these
 *     menus feed resolve in the firm's zone: `ExtendTaskFollowUp` calls `ResolveFirmTimeZoneAsync()`
 *     and stamps the task via `MoveTaskDatesToFireDayAsync(..., firmTz)`, while the toolbar Snooze
 *     sends a floating `yyyy-MM-dd` that becomes the task's Start date. So a user whose browser sits
 *     across midnight from the company saw a "Tomorrow" the server did not agree with.
 *  2. **They added milliseconds, not days** (`now.getTime() + n * 86400000`). Across a DST
 *     transition that lands an hour early or late, which rolls to the wrong calendar day whenever
 *     the menu is opened near midnight. `moment().add(n, 'day')` is calendar arithmetic and is safe.
 *
 * Both are fixed here once, for every caller.
 */
@Injectable({ providedIn: 'root' })
export class TaskDatePresetsService {

    constructor(private _companyNow: CompanyNowService) { }

    /**
     * The company's today as a date-only moment — the base every preset counts from, and what the
     * calendars take as `todayDate` / `minDate`.
     */
    today(): moment.Moment {
        return moment(this._companyNow.today(), 'YYYY-MM-DD');
    }

    /**
     * Resolve each spec against `today` (the company's, unless a caller passes the one it already
     * holds so the menu and its calendar can't disagree).
     */
    resolve(specs: DatePresetSpec[], today?: moment.Moment): ResolvedDatePreset[] {
        const base = today || this.today();
        return specs.map(spec => {
            const offset = typeof spec.offsetDays === 'function' ? spec.offsetDays(base) : spec.offsetDays;
            const date = base.clone().add(offset, 'day');
            return {
                key: spec.key,
                label: spec.label,
                date: date,
                weekday: date.format('ddd'),
                dateLabel: date.format('MMM D'),
                iso: date.format('YYYY-MM-DD')
            };
        });
    }
}
